import React, { useState, useRef } from 'react';
import { Mail, Phone, MapPin, Send, User } from 'lucide-react';
import { FaFacebook, FaInstagram, FaLinkedin, FaWhatsapp } from 'react-icons/fa';

import SEO from '../components/SEO';
import { useData } from '../context/DataContext';
import FadeIn from '../components/FadeIn';
import { useTranslation } from 'react-i18next';

const Contact: React.FC = () => {
  const { legalPages } = useData();
  const { t, i18n } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const form = useRef<HTMLFormElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Honeypot Check (Bot Koruması)
    if (form.current) {
      const formData = new FormData(form.current);
      if (formData.get('bot_check')) {
        console.log('Bot detected, submission blocked.');
        return; // Bot ise işlemi durdur
      }
    }

    setFormStatus('sending');

    if (form.current) {
      const formData = new FormData(form.current);
      const data = {
        name: formData.get('user_name'),
        email: formData.get('user_email'),
        phone: formData.get('user_phone'),
        subject: formData.get('subject'),
        message: formData.get('message'),
        language: i18n.language || 'tr'
      };

      const apiUrl = import.meta.env.VITE_API_URL || '/backend';

      fetch(`${apiUrl}/api/contact.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        console.log(data.message);
        setFormStatus('success');
      })
      .catch((error) => {
        console.error('There was a problem with the fetch operation:', error);
        setFormStatus('error');
        alert('Mesaj gönderilirken bir hata oluştu. Lütfen daha sonra tekrar deneyiniz.');
      });
    }
  };

  return (
    <div className="w-full bg-brand-light">
      <SEO
        pageCode="contact"
        title="İletişim"
        description="Pato Yazılım ile iletişime geçin. Projeniz için teklif alın veya sorularınızı sorun."
        link="/iletisim"
      />
      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-brand-dark text-center">
        <FadeIn className="container mx-auto px-4 md:px-8">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            {t('contact.heroTitle')}
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            {t('contact.heroDesc')}
          </p>
        </FadeIn>
      </section>

      <div className="container mx-auto px-4 md:px-8 py-16">
        <div className="grid lg:grid-cols-5 gap-12 bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Info Side */}
          <FadeIn className="lg:col-span-2 bg-brand-dark text-white p-10 flex flex-col justify-between relative overflow-hidden" direction="left">
            <div className="absolute top-0 right-0 w-64 h-64 bg-brand-blue/20 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>

            <div className="relative z-10">
              <h3 className="text-2xl font-bold mb-8">{t('contact.contactInfo')}</h3>
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                    <Phone className="text-brand-blue" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{t('contact.phone')}</p>
                    <p className="font-medium">+90 545 276 17 46</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                    <Mail className="text-brand-blue" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{t('contact.email')}</p>
                    <a href="mailto:info@patoyazilim.com" className="font-medium hover:text-brand-blue transition-colors">info@patoyazilim.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center shrink-0">
                    <MapPin className="text-brand-blue" />
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-1">{t('contact.address')}</p>
                    <p className="font-medium leading-relaxed">
                      Bozkurt Mah. Seymen Sk. No:43<br />
                      D:3 Şişli/İstanbul
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative z-10 mt-12">
              <p className="text-sm text-gray-400 mb-4">{t('common.followUs')}</p>
              <div className="flex gap-4">
                <a href="https://www.instagram.com/patoyazilim" target="_blank" rel="noreferrer" aria-label="Instagram sayfamız" className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand-blue flex items-center justify-center transition-colors">
                  <FaInstagram size={18} />
                </a>
                <a href="https://www.linkedin.com/company/pato-yazilim" target="_blank" rel="noreferrer" aria-label="LinkedIn sayfamız" className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand-blue flex items-center justify-center transition-colors">
                  <FaLinkedin size={18} />
                </a>
                <a href="https://www.facebook.com/patoyazilim" target="_blank" rel="noreferrer" aria-label="Facebook sayfamız" className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand-blue flex items-center justify-center transition-colors">
                  <FaFacebook size={18} />
                </a>
                <a href="https://wa.me/message/RWB5MCIBD74WH1" target="_blank" rel="noreferrer" aria-label="WhatsApp iletişim" className="w-10 h-10 rounded-full bg-white/10 hover:bg-brand-blue flex items-center justify-center transition-colors">
                  <FaWhatsapp size={18} />
                </a>
              </div>
            </div>
          </FadeIn>

          {/* Form Side */}
          <FadeIn className="lg:col-span-3 p-10" direction="right" delay={0.2}>
            {formStatus === 'success' ? (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mb-6">
                  <Send size={40} />
                </div>
                <h3 className="text-2xl font-bold text-brand-dark mb-2">{t('contact.formSuccessTitle')}</h3>
                <p className="text-gray-700">{t('contact.formSuccessDesc')}</p>
                <button
                  onClick={() => setFormStatus('idle')}
                  className="mt-8 text-brand-blue font-medium hover:underline"
                >
                  {t('contact.formNewMessage')}
                </button>
              </div>
            ) : (
              <form ref={form} onSubmit={handleSubmit} className="space-y-6">
                {/* Honeypot Field (Hidden) */}
                <div className="hidden opacity-0 absolute -z-10">
                  <input type="text" name="bot_check" tabIndex={-1} autoComplete="off" />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formNameLabel')}</label>
                    <input
                      type="text"
                      name="user_name"
                      required
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all"
                      placeholder={t('contact.formNamePlaceholder')}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formPhoneLabel')}</label>
                    <input
                      type="tel"
                      name="user_phone"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all"
                      placeholder={t('contact.formPhonePlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formEmailLabel')}</label>
                  <input
                    type="email"
                    name="user_email"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all"
                    placeholder={t('contact.formEmailPlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formSubjectLabel')}</label>
                  <select name="subject" className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all bg-white">
                    <option value="Web Sitesi Teklifi">{t('contact.formSubjectOptions.web')}</option>
                    <option value="Mobil Uygulama">{t('contact.formSubjectOptions.mobile')}</option>
                    <option value="Danışmanlık">{t('contact.formSubjectOptions.consultancy')}</option>
                    <option value="Diğer">{t('contact.formSubjectOptions.other')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('contact.formMessageLabel')}</label>
                  <textarea
                    name="message"
                    rows={4}
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 outline-none transition-all resize-none"
                    placeholder={t('contact.formMessagePlaceholder')}
                  ></textarea>
                </div>

                {/* Dinamik Yasal Metin Onay Kutusu */}
                {legalPages.filter(p => p.showInContactForm).length > 0 && (
                  <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-lg border border-slate-100">
                    <input 
                      type="checkbox" 
                      id="legal_consent" 
                      name="legal_consent" 
                      required 
                      className="mt-1 w-4 h-4 text-brand-blue rounded border-gray-300 focus:ring-brand-blue"
                    />
                    <label htmlFor="legal_consent" className="text-sm text-gray-600">
                      {t('contact.legalConsentText1')}
                      {legalPages.filter(p => p.showInContactForm).map((page, idx, arr) => {
                        const path = i18n.language === 'en' ? `/en/${page.slug}` : `/${page.slug}`;
                        return (
                          <React.Fragment key={page.id}>
                            <a href={path} target="_blank" rel="noreferrer" className="text-brand-blue font-semibold hover:underline">
                              {page.title}
                            </a>
                            {idx === arr.length - 2 ? t('contact.legalConsentAnd') : idx < arr.length - 2 ? ', ' : ''}
                          </React.Fragment>
                        );
                      })}
                      {t('contact.legalConsentText2')}
                    </label>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="w-full bg-brand-blue hover:bg-blue-600 text-white font-bold py-4 rounded-lg shadow-lg hover:shadow-brand-blue/30 transition-all transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
                >
                  {formStatus === 'sending' ? (
                    t('contact.formSending')
                  ) : (
                    <>
                      {t('contact.formSubmit')} <Send size={18} />
                    </>
                  )}
                </button>
              </form>
            )}
          </FadeIn>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 md:px-8 text-center">
          <FadeIn className="text-4xl md:text-5xl font-bold text-brand-dark mb-16">
            {t('contact.featuresTitle1')} <span className="text-brand-blue">{t('contact.featuresTitle2')}</span> {t('contact.featuresTitle3')}
          </FadeIn>

          <div className="grid md:grid-cols-3 gap-12">
            {/* Feature 1 */}
            <FadeIn className="flex flex-col items-center group" delay={0.1}>
              <div className="w-24 h-24 rounded-3xl flex items-center justify-center mb-6 text-[#353589] group-hover:text-[#3e73fe] transition-all duration-300 transform group-hover:scale-110">
                <Mail size={64} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-brand-blue mb-4">{t('contact.feature1Title')}</h3>
              <p className="text-gray-700 leading-relaxed mb-4 max-w-xs">
                {t('contact.feature1Desc')}
              </p>
            </FadeIn>

            {/* Feature 2 */}
            <FadeIn className="flex flex-col items-center group" delay={0.2}>
              <div className="w-24 h-24 rounded-3xl flex items-center justify-center mb-6 text-[#353589] group-hover:text-[#3e73fe] transition-all duration-300 transform group-hover:scale-110">
                <MapPin size={64} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-brand-blue mb-4">{t('contact.feature2Title')}</h3>
              <p className="text-gray-700 leading-relaxed mb-4 max-w-xs">
                {t('contact.feature2Desc')}
              </p>
            </FadeIn>

            {/* Feature 3 */}
            <FadeIn className="flex flex-col items-center group" delay={0.3}>
              <div className="w-24 h-24 rounded-3xl flex items-center justify-center mb-6 text-[#353589] group-hover:text-[#3e73fe] transition-all duration-300 transform group-hover:scale-110">
                <User size={64} strokeWidth={1.5} />
              </div>
              <h3 className="text-xl font-bold text-brand-blue mb-4">{t('contact.feature3Title')}</h3>
              <p className="text-gray-700 leading-relaxed mb-4 max-w-xs">
                {t('contact.feature3Desc')}
              </p>
            </FadeIn>
          </div>
        </div>
      </section>
    </div >
  );
};

export default Contact;